import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import addressApi from "api/landlord/address";
import {
  ApplyCustomButtonLinkResponse,
  CustomButtonLinkScope
} from "types/landlord/address";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { targetLabel } from "shared/app/ApplyCustomButtonLink/helpers";
import { isSettledStatus } from "providers/BulkBrandingProgress";

const MAX_CONSECUTIVE_POLL_ERRORS = 10;
const MAX_POLL_DURATION_MS = 30 * 60 * 1000;

const CANCEL_ERROR_MESSAGES: Record<string, string> = {
  not_cancellable: "This run can't be cancelled.",
  already_settled: "This run has already finished."
};

const getCancelErrorMessage = (error: unknown) => {
  const code =
    error instanceof AxiosError ? error.response?.data?.error?.code : undefined;
  return (
    (code && CANCEL_ERROR_MESSAGES[code]) ||
    "Failed to cancel — it may already be finishing up"
  );
};

type BulkCustomButtonLinkRun = {
  scope: CustomButtonLinkScope;
  sourceId: number;
  bulkApplyId: number;
  startedAt: number;
};

const STORAGE_KEY = "bulkCustomButtonLinkProgress.run";

const readStoredRun = (): BulkCustomButtonLinkRun | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const isValid =
      (parsed?.scope === "property" || parsed?.scope === "unit") &&
      typeof parsed?.sourceId === "number" &&
      typeof parsed?.bulkApplyId === "number" &&
      typeof parsed?.startedAt === "number";
    return isValid ? parsed : null;
  } catch {
    return null;
  }
};

const writeStoredRun = (run: BulkCustomButtonLinkRun | null) => {
  try {
    if (run) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(run));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    return;
  }
};

type BulkCustomButtonLinkProgressContext = {
  run: BulkCustomButtonLinkRun | null;
  status: ApplyCustomButtonLinkResponse | undefined;
  isStatusError: boolean;
  hasGivenUpPolling: boolean;
  isCancelling: boolean;
  hasRequestedCancel: boolean;
  startTracking: (run: Omit<BulkCustomButtonLinkRun, "startedAt">) => void;
  cancelRun: () => void;
  dismiss: () => void;
};

const BulkCustomButtonLinkProgressContext =
  createContext<BulkCustomButtonLinkProgressContext>({
    run: null,
    status: undefined,
    isStatusError: false,
    hasGivenUpPolling: false,
    isCancelling: false,
    hasRequestedCancel: false,
    startTracking: () => {},
    cancelRun: () => {},
    dismiss: () => {}
  });

export function BulkCustomButtonLinkProgressProvider({
  children
}: {
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const dictionary = useUnitsPropertiesDictionary();
  const [run, setRun] = useState<BulkCustomButtonLinkRun | null>(readStoredRun);
  const settledRunIdRef = useRef<number | null>(null);
  const hasObservedUnsettledRef = useRef(false);
  const [hasRequestedCancel, setHasRequestedCancel] = useState(false);

  const statusQueryKey = [
    run?.scope === "unit" ? "units" : "properties",
    run?.sourceId,
    "apply_custom_button_link",
    run?.bulkApplyId
  ];

  const { data: status, isError: isStatusError } =
    useQuery<ApplyCustomButtonLinkResponse>({
      queryKey: statusQueryKey,
      queryFn: () =>
        addressApi.getApplyCustomButtonLinkStatus({
          scope: run!.scope,
          sourceId: run!.sourceId,
          bulkApplyId: run!.bulkApplyId
        }),
      enabled: run !== null,
      refetchInterval: (query) => {
        const s = query.state.data?.status;
        if (s && isSettledStatus(s)) return false;
        if (query.state.fetchFailureCount >= MAX_CONSECUTIVE_POLL_ERRORS)
          return false;
        if (run && Date.now() - run.startedAt > MAX_POLL_DURATION_MS)
          return false;
        return 1000;
      }
    });

  const hasGivenUpPolling = Boolean(
    run &&
    status &&
    !isSettledStatus(status.status) &&
    Date.now() - run.startedAt > MAX_POLL_DURATION_MS
  );

  const { mutate: cancelRun, isPending: isCancelling } = useMutation({
    mutationKey: ["BULK", "APPLY", "CUSTOM_BUTTON_LINK", "CANCEL"],
    mutationFn: () =>
      addressApi.cancelApplyCustomButtonLink({
        scope: run!.scope,
        sourceId: run!.sourceId,
        bulkApplyId: run!.bulkApplyId
      }),
    onSuccess: () => {
      setHasRequestedCancel(true);
      queryClient.invalidateQueries({ queryKey: statusQueryKey });
    },
    onError: (mutationError) =>
      toast.error(getCancelErrorMessage(mutationError))
  });

  useEffect(() => {
    if (!status || !run) return;
    if (!isSettledStatus(status.status)) {
      hasObservedUnsettledRef.current = true;
      return;
    }
    if (settledRunIdRef.current === status.id) return;

    settledRunIdRef.current = status.id;
    queryClient.invalidateQueries({ queryKey: ["properties"] });
    if (run.scope === "unit") {
      queryClient.invalidateQueries({ queryKey: ["unit"] });
      queryClient.invalidateQueries({ queryKey: ["units"] });
    }

    if (!hasObservedUnsettledRef.current) return;

    const label = targetLabel(status.updated_count, run.scope, dictionary);
    if (status.status === "cancelled")
      toast.info(
        `Custom button link cancelled — ${status.updated_count} ${label} already updated were kept`
      );
    else if (status.status === "completed" && status.failed.length === 0)
      toast.success(
        `Custom button link applied to ${status.updated_count} ${label}`
      );
  }, [status, run, queryClient, dictionary]);

  const startTracking = useCallback(
    (next: Omit<BulkCustomButtonLinkRun, "startedAt">) => {
      const withStartedAt = { ...next, startedAt: Date.now() };
      settledRunIdRef.current = null;
      hasObservedUnsettledRef.current = true;
      setHasRequestedCancel(false);
      writeStoredRun(withStartedAt);
      setRun(withStartedAt);
    },
    []
  );

  const dismiss = useCallback(() => {
    writeStoredRun(null);
    setRun(null);
  }, []);

  const value = useMemo(
    () => ({
      run,
      status,
      isStatusError,
      hasGivenUpPolling,
      isCancelling,
      hasRequestedCancel,
      startTracking,
      cancelRun,
      dismiss
    }),
    [
      run,
      status,
      isStatusError,
      hasGivenUpPolling,
      isCancelling,
      hasRequestedCancel,
      startTracking,
      cancelRun,
      dismiss
    ]
  );

  return (
    <BulkCustomButtonLinkProgressContext.Provider value={value}>
      {children}
    </BulkCustomButtonLinkProgressContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBulkCustomButtonLinkProgress() {
  return useContext(BulkCustomButtonLinkProgressContext);
}
